// useSchedulerSummary — server-engine adapter for `BulkOrdersPanel`.
//
// Calls `get_batch_scheduler_summary` and falls back to the client engine
// (rules.ts) when the RPC is unreachable/disabled. The hook is the surface
// where Sprint 5 swaps the local heuristics: every consumer renders the
// returned `ServerSchedulerSummary` regardless of source.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildClientFallbackSummary,
  fetchBatchSchedulerSummary,
  trackSchedulerEngineFallback,
  type ClientFallbackInput,
  type SchedulerError,
  type SchedulerSource,
  type ServerSchedulerSummary,
} from "@/lib/scheduler";
import type { UseSchedulerState } from "./types";

export interface UseSchedulerSummaryOptions {
  companyId: string | null | undefined;
  batchId: string | null | undefined;
  /**
   * Synchronous client-side facts used when the server engine is unreachable.
   * Computed from rows already loaded by the panel — no extra IO.
   */
  clientFallback?: () => ClientFallbackInput;
  /** Default `true`. Skip the call when the panel is collapsed. */
  enabled?: boolean;
}

export function useSchedulerSummary(
  options: UseSchedulerSummaryOptions,
): UseSchedulerState<ServerSchedulerSummary> {
  const { companyId, batchId, clientFallback, enabled = true } = options;
  const [data, setData] = useState<ServerSchedulerSummary | null>(null);
  const [source, setSource] = useState<SchedulerSource | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SchedulerError | null>(null);
  const [tick, setTick] = useState(0);
  const fallbackRef = useRef(clientFallback);
  fallbackRef.current = clientFallback;

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !companyId || !batchId) {
      setData(null);
      setSource(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      const result = await fetchBatchSchedulerSummary({ companyId, batchId });
      if (cancelled) return;
      if (result.data) {
        setData(result.data);
        setSource(result.source);
        setError(null);
        setIsLoading(false);
        return;
      }
      const recoverable = !result.error || result.error.recoverable;
      if (recoverable && fallbackRef.current) {
        const fallback = buildClientFallbackSummary(fallbackRef.current());
        setData(fallback);
        setSource("client_fallback");
        setError(null);
        trackSchedulerEngineFallback({
          surface: "bulk_orders_panel",
          scope: "batch",
          reason: result.error?.message ?? "no_data",
          errorCode: result.error?.code,
          recoverable: true,
        });
      } else {
        setData(null);
        setSource(null);
        setError(result.error);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, batchId, enabled, tick]);

  return useMemo(
    () => ({ data, source, isLoading, error, refetch }),
    [data, source, isLoading, error, refetch],
  );
}
